export interface ClientListEntry {
  clientId: string;
  name: string;
  description: string;
  baseUrl: string;
  creationDate: string;
  lastLoginTimestamp: string;
  admins: string[];
}

export interface Client {
  id: string;

  clientId: string;
  enabled: boolean;
  name: string;
  description: string;

  publicClient: boolean;
  bearerOnly: boolean;
  serviceAccountEnabled: boolean;
  clientSecret?: string;

  standardFlowEnabled: boolean;
  enforcePkce: boolean;
  implicitFlowEnabled: boolean;
  directAccessGrantsEnabled: boolean;

  fullScopeAllowed: boolean;

  minimumAcrValue?: number;

  rootUrl: string;
  baseUrl: string;

  webOrigins: string[];
  redirectUris: string[];

  // associated client role that enables viewing / changing of the client
  associatedRole: string;

  lastLogin?: string;
}

export interface ClientList {
  clients: Client[];
  isServicePortalAdmin?: boolean;
}

export interface RoleInput {
  name: string;
  description: string;
  isPublic: boolean;
}

export interface Role extends RoleInput {
  id: string;
  memberCount: number;

  associatedPermissionRole: string;
}

export interface RoleList {
  roles: Role[];
}

export interface RoleDetails {
  role: Role;
  members: RoleMemberList;
  mappedByClientList: SimplifiedClientInfo[];
}

export interface SimplifiedClientInfo {
  id: string;
  clientId: string;
}

export interface Permission {
  name: string;
  members: RoleMember[];
}

export interface PermissionList {
  permissionRoles: Permission[];
}

export interface UserReference {
  id?: string;
  username?: string;
}

export type NewRoleMember = UserReference;

export interface NewManagementRoleMember {
  id?: string;
  username?: string;
  permissionRole?: string;
}

export interface RoleMember {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  lastLogin?: string;
}

export interface ManagementRoleMember extends RoleMember {
  hasServiceportalAccess: boolean;
}

export interface RoleMemberList {
  members: RoleMember[];
}

export interface SessionMapper {
  claimName: string;
  note: string;
}

export interface UserAttributeMapper {
  claimName: string;
  name: string;
}

export interface AudienceMapper {
  client?: string;
  custom?: string;
}

export interface RealmRolesMapper {
  claimName: string;
  rolePrefix: string;
}

export interface ClientRolesMapper {
  claimName: string;
  clientId: string;
  rolePrefix: string;
}

export interface TokenMapperInput {
  id: string;
  displayName: string;

  multivalued: boolean;
  addToIdToken: boolean;
  addToAccessToken: boolean;
  addToUserInfo: boolean;

  // One of the following mapper types needs to be set
  session?: SessionMapper;
  userAttribute?: UserAttributeMapper;
  audience?: AudienceMapper;
  realmRoles?: RealmRolesMapper;
  clientRoles?: ClientRolesMapper;
}

export interface TokenMapper extends TokenMapperInput {
  id: string;
}

export interface TokenMapperList {
  mappers: TokenMapper[];
}

export interface RoleMapper {
  roleId: string;
  roleName: string;
  roleType: 'client' | 'realm';
  containerId: string;
  clientId: string;
}

export interface RoleMapperListInput {
  mappers: RoleMapper[];
}

export interface RoleMapperList extends RoleMapperListInput {
  available: RoleMapper[];
}

export interface ClientDetails {
  client: Client;
  roles: RoleList;
  mappers?: TokenMapperList;
  roleMappers?: RoleMapperList;
  secrets?: ClientSecrets;
  permissions?: PermissionList;
  testUsers?: TestUserList;
}

// Shared (?) user account interfaces
// May not be needed if the test users look entirely different
export interface UserCredentials {
  password?: string;
  temporary?: boolean;

  removeOtps?: boolean;
}

export interface UserAccountInput {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  emailVerified: boolean;
  note: string;

  credentials?: UserCredentials;
}

export interface UserAccount extends UserAccountInput {
  id: string;
  creationTimestamp: string;
}

export type Salutation = 'mr' | 'ms' | '-';

// Customer account manager
export interface CustomerAccountInput extends UserAccountInput {
  type: string;
  ssuCode?: string;
  salutation?: Salutation;
}

export interface CustomerAccount extends CustomerAccountInput, UserAccount {
  customerNumber?: string;
  customerName?: string;
  lastLogin?: string;
  lastEmailVerificationTriggered?: string;

  hasOtpSetup?: boolean;
}

export interface CustomerAccountList {
  accounts: CustomerAccount[];
}

export interface CustomerSearchCriteria {
  query?: string;
  ssuCode?: string;
  customerNumber?: string;
  enabled?: boolean;

  offset?: number;
  limit?: number;
}

export interface SsuList {
  ssus: string[];
}

export interface ClientSecrets {
  hasSecrets: boolean;
  secret?: string;
  rotatedSecret?: string;
  rotatedSecretCreationTime?: number;
  rotatedSecretExpirationTime?: number;
  hasRotatedSecret: boolean;
}

export interface ScopeEvaluation {
  accessToken: string;
  idToken: string;
}

export interface ClientEntryList {
  clients: ClientListEntry[];
}

export interface AssociatedClientInput {
  id: string;
}

export interface AssociatedClient extends AssociatedClientInput {
  clientId: string;
}

export type AttributeMap = Record<string, string[]>;

export interface TestUserInput extends UserAccountInput {
  owners: UserReference[];
  attributes: AttributeMap;
  associatedClients: AssociatedClient[];
  addToSickEmployeeGroup: boolean;
}

export interface TestUser extends TestUserInput {
  id: string;
  lastLogin?: string;
}

export interface TestUserList {
  users: TestUser[];
}

export interface UserSearchResult {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface UserSearchResultList {
  users: UserSearchResult[];
}

function isClientListEntry(obj: unknown): obj is ClientListEntry {
  if (!isObject(obj)) {
    return false;
  }

  if (typeof obj['clientId'] !== 'string') {
    return false;
  }
  if (!isStringOrDefault(obj, 'name', '')) {
    return false;
  }
  if (!isStringOrDefault(obj, 'description', '')) {
    return false;
  }
  if (!isStringOrDefault(obj, 'baseUrl', '')) {
    return false;
  }
  if (!isStringOrDefault(obj, 'creationDate', '')) {
    return false;
  }
  if (!isStringOrDefault(obj, 'lastLoginTimestamp', '')) {
    return false;
  }
  return isStringArray(obj['admins']);
}

export function isClientEntryList(obj: unknown): obj is ClientEntryList {
  if (!isObject(obj)) {
    return false;
  }
  if (!Array.isArray(obj['clients'])) {
    return false;
  }

  return obj['clients'].every((e) => isClientListEntry(e));
}

export function isClient(obj: unknown): obj is Client {
  if (!isObject(obj)) {
    return false;
  }

  if (typeof obj['id'] !== 'string') {
    return false;
  }

  if (typeof obj['clientId'] !== 'string') {
    return false;
  }
  if (typeof obj['enabled'] !== 'boolean') {
    return false;
  }
  if (!isStringOrDefault(obj, 'name', '')) {
    return false;
  }
  if (!isStringOrDefault(obj, 'description', '')) {
    return false;
  }

  if (typeof obj['publicClient'] !== 'boolean') {
    return false;
  }
  if (typeof obj['bearerOnly'] !== 'boolean') {
    return false;
  }
  if (typeof obj['serviceAccountEnabled'] !== 'boolean') {
    return false;
  }
  if (obj['clientSecret'] && typeof obj['clientSecret'] !== 'string') {
    return false;
  }

  if (typeof obj['standardFlowEnabled'] !== 'boolean') {
    return false;
  }
  if (typeof obj['enforcePkce'] !== 'boolean') {
    return false;
  }
  if (typeof obj['implicitFlowEnabled'] !== 'boolean') {
    return false;
  }
  if (typeof obj['directAccessGrantsEnabled'] !== 'boolean') {
    return false;
  }
  if (typeof obj['fullScopeAllowed'] !== 'boolean') {
    return false;
  }

  if (!isStringOrDefault(obj, 'rootUrl', '')) {
    return false;
  }
  if (!isStringOrDefault(obj, 'baseUrl', '')) {
    return false;
  }

  if (!isStringArray(obj['webOrigins'])) {
    return false;
  }
  return isStringArray(obj['redirectUris']);
}

export function isClientList(obj: unknown): obj is ClientList {
  if (!isObject(obj)) {
    return false;
  }

  return isClientArray(obj['clients']);
}

export function isClientArray(obj: unknown): obj is Client[] {
  if (!Array.isArray(obj)) {
    return false;
  }

  return obj.every(isClient);
}

export function isClientSecrets(obj: unknown): obj is ClientSecrets {
  if (!isObject(obj)) {
    return false;
  }
  if (typeof obj['hasSecrets'] !== 'boolean') return false;
  if (typeof obj['secret'] !== 'string') return false;
  if (obj['rotatedSecret'] && typeof obj['rotatedSecret'] !== 'string') return false;
  if (obj['rotatedSecretCreationTime'] && typeof obj['rotatedSecretCreationTime'] !== 'number') return false;
  if (obj['rotatedSecretExpirationTime'] && typeof obj['rotatedSecretExpirationTime'] !== 'number') return false;
  if (typeof obj['hasRotatedSecret'] !== 'boolean') return false;

  return true;
}

export function isScopeEvaluation(obj: unknown): obj is ScopeEvaluation {
  if (!isObject(obj)) {
    return false;
  }
  if (!isToken(obj['accessToken'])) {
    return false;
  }
  return isToken(obj['idToken']);
}

export function isToken(obj: unknown) {
  if (!isObject(obj)) {
    return false;
  }
  // check some typical token fields
  if (typeof obj['iat'] !== 'number') return false;
  if (typeof obj['jti'] !== 'string') return false;
  if (typeof obj['iss'] !== 'string') return false;
  if (typeof obj['typ'] !== 'string') return false;

  return true;
}

export function isRole(obj: unknown): obj is Role {
  if (!isObject(obj)) {
    return false;
  }

  if (typeof obj['id'] !== 'string') return false;
  if (typeof obj['name'] !== 'string') return false;
  if (typeof obj['description'] !== 'string') return false;
  if (typeof obj['isPublic'] !== 'boolean') return false;
  return typeof obj['memberCount'] === 'number';
}

export function isRoleList(obj: unknown): obj is RoleList {
  if (!isObject(obj)) return false;

  if (!Array.isArray(obj['roles'])) return false;

  return obj['roles'].every(isRole);
}

export function isRoleDetails(obj: unknown): obj is RoleDetails {
  if (!isObject(obj)) {
    return false;
  }

  if (!isRole(obj['role'])) return false;
  if (!isRoleMemberList(obj['members'])) return false;

  if (!Array.isArray(obj['mappedByClientList'])) return false;
  if (!obj['mappedByClientList'].every(isSimplifiedClientInfo)) return false;

  return true;
}

export function isSimplifiedClientInfo(obj: unknown): obj is SimplifiedClientInfo {
  if (!isObject(obj)) {
    return false;
  }

  if (typeof obj['id'] !== 'string') return false;
  if (typeof obj['clientId'] !== 'string') return false;

  return true;
}

export function isPermission(obj: unknown): obj is Permission {
  if (!isObject(obj)) {
    return false;
  }

  if (!Array.isArray(obj['members'])) return false;

  // if (typeof (obj['id']) !== 'string') return false;
  return typeof obj['name'] == 'string';
}

export function isPermissionList(obj: unknown): obj is PermissionList {
  if (!isObject(obj)) return false;

  if (!Array.isArray(obj['permissionRoles'])) return false;

  return obj['permissionRoles'].every(isPermission);
}

export function isRoleMember(obj: unknown): obj is RoleMember {
  if (!isObject(obj)) return false;

  if (typeof obj['id'] !== 'string') return false;
  if (typeof obj['username'] !== 'string') return false;

  if (obj['email'] && typeof obj['email'] !== 'string') return false;
  if (obj['firstName'] && typeof obj['firstName'] !== 'string') return false;
  if (obj['lastName'] && typeof obj['lastName'] !== 'string') return false;

  return !(obj['lastLogin'] && typeof obj['lastLogin'] !== 'string');
}

export function isManagementRoleMember(obj: unknown): obj is ManagementRoleMember {
  if (!isObject(obj)) return false;
  if (typeof obj['hasServiceportalAccess'] !== 'boolean') return false;
  return isRoleMember(obj);
}

export function isRoleMemberList(obj: unknown): obj is RoleMemberList {
  if (!isObject(obj)) return false;

  if (!Array.isArray(obj['members'])) return false;

  return obj['members'].every(isRoleMember);
}

function isSessionMapper(obj: unknown): obj is SessionMapper {
  if (!isObject(obj)) return false;

  if (typeof obj['claimName'] !== 'string') return false;

  return typeof obj['note'] === 'string';
}

function isUserAttributeMapper(obj: unknown): obj is UserAttributeMapper {
  if (!isObject(obj)) return false;

  if (typeof obj['claimName'] !== 'string') return false;

  return typeof obj['name'] === 'string';
}

function isAudienceMapper(obj: unknown): obj is AudienceMapper {
  if (!isObject(obj)) return false;

  if (obj['client'] && typeof obj['client'] !== 'string') return false;
  if (obj['custom'] && typeof obj['custom'] !== 'string') return false;

  return true;
}

function isRealmRolesMapper(obj: unknown): obj is RealmRolesMapper {
  if (!isObject(obj)) return false;

  return !obj['rolePrefix'] || typeof obj['rolePrefix'] === 'string';
}

function isClientRolesMapper(obj: unknown): obj is ClientRolesMapper {
  if (!isObject(obj)) return false;

  if (typeof obj['clientId'] !== 'string') return false;

  return !obj['rolePrefix'] || typeof obj['rolePrefix'] === 'string';
}

export function isTokenMapper(obj: unknown): obj is TokenMapper {
  if (!isObject(obj)) return false;

  if (typeof obj['id'] !== 'string') return false;
  if (typeof obj['displayName'] !== 'string') return false;

  if (typeof obj['multivalued'] !== 'boolean') return false;
  if (typeof obj['addToIdToken'] !== 'boolean') return false;
  if (typeof obj['addToAccessToken'] !== 'boolean') return false;
  if (typeof obj['addToUserInfo'] !== 'boolean') return false;

  if (obj['session'] && !isSessionMapper(obj['session'])) return false;
  if (obj['userAttribute'] && !isUserAttributeMapper(obj['userAttribute'])) return false;
  if (obj['audience'] && !isAudienceMapper(obj['audience'])) return false;
  if (obj['realmRoles'] && !isRealmRolesMapper(obj['realmRoles'])) return false;
  return !(obj['clientRoles'] && !isClientRolesMapper(obj['clientRoles']));
}

export function isTokenMapperList(obj: unknown): obj is TokenMapperList {
  if (!isObject(obj)) return false;

  if (!Array.isArray(obj['mappers'])) return false;

  return obj['mappers'].every(isTokenMapper);
}

export function isRoleMapper(obj: unknown): obj is RoleMapper {
  if (!isObject(obj)) return false;

  if (typeof obj['roleId'] !== 'string') return false;
  if (typeof obj['roleName'] !== 'string') return false;
  if (typeof obj['roleType'] !== 'string') return false;
  if (typeof obj['containerId'] !== 'string') return false;
  return typeof obj['clientId'] === 'string';
}

export function isRoleMapperList(obj: unknown): obj is RoleMapperList {
  if (!isObject(obj)) return false;

  if (!Array.isArray(obj['mappers'])) return false;
  if (!obj['mappers'].every(isRoleMapper)) return false;

  if (!Array.isArray(obj['available'])) return false;
  if (!obj['available'].every(isRoleMapper)) return false;

  return true;
}

export function isClientDetails(obj: unknown): obj is ClientDetails {
  if (!isObject(obj)) {
    return false;
  }

  if (!isClient(obj['client'])) return false;
  if (!isRoleList(obj['roles'])) return false;
  if (obj['mappers'] && !isTokenMapperList(obj['mappers'])) return false;
  if (obj['roleMappers'] && !isRoleMapperList(obj['roleMappers'])) return false;
  if (obj['secrets'] && !isClientSecrets(obj['secrets'])) return false;
  if (obj['permissions'] && !isPermissionList(obj['permissions'])) return false;

  return true;
}

export function isUserCredentials(obj: unknown): obj is UserCredentials {
  if (!isObject(obj)) return false;

  if (obj['password'] && typeof obj['password'] !== 'string') return false;
  if (obj['temporary'] && typeof obj['temporary'] !== 'boolean') return false;

  if (obj['removeOtps'] && typeof obj['removeOtps'] !== 'boolean') return false;

  return true;
}

function isUserAccount(obj: unknown): obj is UserAccount {
  if (!isObject(obj)) return false;

  if (typeof obj['id'] !== 'string') return false;
  if (typeof obj['creationTimestamp'] !== 'string') return false;
  if (typeof obj['username'] !== 'string') return false;
  if (obj['email'] && typeof obj['email'] !== 'string') return false;
  if (obj['firstName'] && typeof obj['firstName'] !== 'string') return false;
  if (obj['lastName'] && typeof obj['lastName'] !== 'string') return false;
  if (typeof obj['enabled'] !== 'boolean') return false;
  if (typeof obj['emailVerified'] !== 'boolean') return false;
  return !obj['credentials'] || isUserCredentials(obj['credentials']);
}

function isSalutation(obj: unknown): obj is Salutation {
  return obj === 'mr' || obj === 'ms' || obj === '-';
}

// Customer account manager
export function isCustomerAccount(obj: unknown): obj is CustomerAccount {
  if (!isObject(obj)) return false;

  if (typeof obj['type'] !== 'string') return false;
  if (typeof obj['note'] !== 'string') return false;

  if (obj['customerNumber'] && typeof obj['customerNumber'] !== 'string') return false;
  if (obj['customerName'] && typeof obj['customerName'] !== 'string') return false;
  if (obj['ssuCode'] && typeof obj['ssuCode'] !== 'string') return false;
  if (obj['lastLogin'] && typeof obj['lastLogin'] !== 'string') return false;
  if (obj['salutation'] && !isSalutation(obj['salutation'])) return false;
  if (obj['hasOtpSetup'] && typeof obj['hasOtpSetup'] !== 'boolean') return false;
  if (obj['lastEmailVerificationTriggered'] && typeof obj['lastEmailVerificationTriggered'] !== 'string') return false;

  return isUserAccount(obj);
}

export function isCustomerAccountList(obj: unknown): obj is CustomerAccountList {
  if (!isObject(obj)) return false;

  if (!Array.isArray(obj['accounts'])) return false;

  return obj['accounts'].every(isCustomerAccount);
}

export function isSsuList(obj: unknown): obj is SsuList {
  if (!isObject(obj)) return false;

  return isStringArray(obj['ssus']);
}

export function isAssociatedClient(obj: unknown): obj is AssociatedClient {
  if (!isObject(obj)) return false;

  if (typeof obj['id'] !== 'string') return false;
  if (typeof obj['clientId'] !== 'string') return false;

  return true;
}

export function isAssociatedClientList(obj: unknown): obj is AssociatedClient {
  if (!Array.isArray(obj)) return false;

  return obj.every((e) => isAssociatedClient(e));
}

export function isUserReference(obj: unknown): obj is UserReference {
  if (!isObject(obj)) return false;

  if (typeof obj['id'] !== 'string') return false;
  if (typeof obj['username'] !== 'string') return false;

  return true;
}

export function isUserReferenceList(obj: unknown): obj is UserReference[] {
  if (!Array.isArray(obj)) return false;

  return obj.every((e) => isUserReference(e));
}

export function isAttributeMap(obj: unknown): obj is Record<string, string[]> {
  if (!isObject(obj)) return false;

  for (const key in obj) {
    const value = obj[key];
    if (!isStringArray(value)) return false;
  }

  return true;
}

export function isTestUser(obj: unknown): obj is TestUser {
  if (!isObject(obj)) return false;

  if (!isUserReferenceList(obj['owners'])) return false;
  if (!isAttributeMap(obj['attributes'])) return false;
  if (!isAssociatedClientList(obj['associatedClients'])) return false;

  if (typeof obj['note'] !== 'string') return false;
  if (obj['lastLogin'] && typeof obj['lastLogin'] !== 'string') return false;

  return true;
}

export function isTestUserList(obj: unknown): obj is TestUserList {
  if (!isObject(obj)) return false;

  if (!Array.isArray(obj['users'])) return false;

  return obj['users'].every((e) => isTestUser(e));
}

export function isUserSearchResult(obj: unknown): obj is UserSearchResult {
  if (!isObject(obj)) return false;

  if (typeof obj['id'] !== 'string') return false;
  if (typeof obj['username'] !== 'string') return false;
  if (typeof obj['email'] !== 'string') return false;
  if (typeof obj['firstName'] !== 'string') return false;
  if (typeof obj['lastName'] !== 'string') return false;

  return true;
}

export function isUserSearchResultList(obj: unknown): obj is UserSearchResultList {
  if (!isObject(obj)) return false;

  if (!Array.isArray(obj['users'])) return false;

  return obj['users'].every((e) => isUserSearchResult(e));
}

export interface AuditIssue {
  level: string;
  reason: string;
  subject: string;
  subjectId: string;
  subjectName: string;
  data?: string;
}

export function isAuditIssue(obj: unknown): obj is AuditIssue {
  if (!isObject(obj)) return false;

  if (typeof obj['level'] !== 'string') return false;
  if (typeof obj['reason'] !== 'string') return false;
  if (typeof obj['subject'] !== 'string') return false;
  if (typeof obj['subjectId'] !== 'string') return false;
  if (typeof obj['subjectName'] !== 'string') return false;
  if (obj['data'] && typeof obj['data'] !== 'string') return false;

  return true;
}

export interface AuditResult {
  isServicePortalAdmin?: boolean;
  issues: AuditIssue[];
}

export function isAuditResult(obj: unknown): obj is AuditResult {
  if (!isObject(obj)) return false;

  if (!Array.isArray(obj['issues'])) return false;

  return obj['issues'].every(isAuditIssue);
}

function isObject(obj: unknown): obj is Record<string, unknown> {
  return typeof obj === 'object' && obj != null;
}

function isStringArray(obj: unknown): obj is string[] {
  if (!Array.isArray(obj)) {
    return false;
  }
  return obj.every((e) => typeof e === 'string');
}

function isStringOrDefault(obj: Record<string, unknown>, field: string, default_: string) {
  if (!obj[field]) {
    obj[field] = default_;
  }
  return typeof obj[field] === 'string';
}
